import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Instagram, Youtube, Mail, Phone } from "lucide-react";

const FloatingCard = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    whileHover={{ y: -8, transition: { duration: 0.4 } }}
    className="relative p-8 bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-[2rem] shadow-2xl group hover:bg-white/[0.05] transition-colors duration-500"
  >
    {/* Subtle Glow Effect on Hover */}
    <div className="absolute inset-0 rounded-[2rem] bg-ayur-400/0 group-hover:bg-ayur-400/[0.02] blur-2xl transition-all duration-700 pointer-events-none" />
    {children}
  </motion.div>
);

export default function Footer() {
  return (
    <footer className="relative bg-ayur-950 pt-32 pb-16 overflow-hidden">
      
      {/* Cinematic Background Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-ayur-950 via-[#0a2b20] to-ayur-950" />
        {/* Vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(15,61,46,0.4)_100%)]" />
        {/* Texture Overlay */}
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none mix-blend-overlay" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.85%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22/%3E%3C/svg%3E")' }} />
      </div>

      <div className="relative z-10 max-w-[90rem] mx-auto px-6 lg:px-12">
        
        {/* Top Section: Floating Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-24">
          
          {/* Brand Card */}
          <FloatingCard delay={0}>
            <div className="flex flex-col h-full">
              <Link to="/" className="flex flex-col gap-1 group w-fit mb-6">
                <span className="font-serif text-3xl font-medium text-white tracking-wide leading-none group-hover:text-ayur-400 transition-colors duration-500 uppercase">
                  VASEEGRAH
                </span>
                <span className="text-[9px] font-sans font-bold text-ayur-400 uppercase tracking-[0.4em] leading-none">
                  Herbal Wellness
                </span>
              </Link>
              <p className="text-ayur-100/60 font-sans font-light leading-relaxed text-sm">
                Pioneering women-led herbal wellness from the heart of Thanjavur. Preserving authentic traditional wisdom through 100% natural formulations.
              </p>
            </div>
          </FloatingCard>

          {/* Navigation Card */}
          <FloatingCard delay={0.1}>
            <h4 className="text-[11px] font-sans font-bold uppercase tracking-[0.3em] text-ayur-400 mb-8">Navigation</h4>
            <nav className="flex flex-col gap-4">
              {['Home', 'About', 'Philosophy', 'Ecosystem', 'Journal', 'Contact'].map((item) => (
                <a 
                  key={item} 
                  href={item === 'Home' ? '/' : `/#${item.toLowerCase()}`} 
                  className="text-sm font-sans font-light text-ayur-50 hover:text-ayur-400 transition-all duration-300 w-fit relative group/link"
                >
                  {item}
                  <span className="absolute bottom-[-2px] left-0 w-0 h-px bg-ayur-400 transition-all duration-300 group-hover/link:w-full" />
                </a>
              ))}
            </nav>
          </FloatingCard>

          {/* Contact Card */}
          <FloatingCard delay={0.2}>
            <h4 className="text-[11px] font-sans font-bold uppercase tracking-[0.3em] text-ayur-400 mb-8">Connect</h4>
            <div className="flex flex-col gap-6 text-sm font-sans font-light text-ayur-50">
              <a href="tel:9786424450" className="flex items-center gap-4 group/contact">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-ayur-400 group-hover/contact:bg-ayur-400 group-hover/contact:text-ayur-950 transition-all duration-500">
                  <Phone size={16} />
                </div>
                <span className="group-hover/contact:text-ayur-400 transition-colors">+91 97864 24450</span>
              </a>
              <a href="mailto:vaseegrahveda@gmail.com" className="flex items-center gap-4 group/contact">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-ayur-400 group-hover/contact:bg-ayur-400 group-hover/contact:text-ayur-950 transition-all duration-500">
                  <Mail size={16} />
                </div>
                <span className="group-hover/contact:text-ayur-400 transition-colors">vaseegrahveda@gmail.com</span>
              </a>
            </div>
          </FloatingCard>

          {/* Social Card */}
          <FloatingCard delay={0.3}>
            <h4 className="text-[11px] font-sans font-bold uppercase tracking-[0.3em] text-ayur-400 mb-8">Follow Us</h4>
            <p className="text-ayur-100/60 text-sm mb-8 font-sans font-light">Join our journey towards a chemical-free lifestyle.</p>
            <div className="flex items-center gap-5">
              {[
                { Icon: Instagram, href: "https://instagram.com/vaseegrah" },
                { Icon: Youtube, href: "https://youtube.com/vaseegrah" }
              ].map(({ Icon, href }, i) => (
                <a 
                  key={i}
                  href={href} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="w-14 h-14 bg-white/5 border border-white/10 rounded-full flex items-center justify-center text-white hover:bg-ayur-400 hover:text-ayur-950 hover:border-ayur-400 transition-all duration-500 hover:shadow-[0_0_20px_rgba(252,176,66,0.3)] group/social"
                >
                  <Icon size={20} className="group-hover/social:scale-110 transition-transform" />
                </a>
              ))}
            </div>
          </FloatingCard>

        </div>

        {/* Bottom Section: Copyright & Links */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.6 }}
          className="flex flex-col md:flex-row items-center justify-between pt-12 border-t border-white/5 gap-6"
        >
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-6 text-[10px] font-sans font-bold uppercase tracking-[0.2em] text-ayur-100/30">
            <span>&copy; {new Date().getFullYear()} Vaseegrah Veda</span>
            <div className="hidden md:block w-1 h-1 rounded-full bg-ayur-400/30" />
            <span>Preserving Traditional Wisdom</span>
            <div className="hidden md:block w-1 h-1 rounded-full bg-ayur-400/30" />
            <a
              href="https://www.techvaseegrah.com/"
              target="_blank"
              rel="noreferrer"
              className="hover:text-ayur-400 transition-colors"
            >
              Powered by TechVaseegrah
            </a>
          </div>
          <div className="flex items-center gap-8 text-[10px] font-sans font-bold uppercase tracking-[0.2em] text-ayur-100/30">
            <a href="#privacy" className="hover:text-ayur-400 transition-colors">Privacy</a>
            <a href="#terms" className="hover:text-ayur-400 transition-colors">Terms</a>
          </div>
        </motion.div>

      </div>

      {/* Decorative Light Glows */}
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-ayur-400/5 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute top-1/2 right-0 -translate-y-1/2 w-64 h-64 bg-ayur-400/5 blur-[100px] rounded-full pointer-events-none" />
    </footer>
  );
}
